import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthToken } from './authToken';
import PrestaShopSettings from './PrestaShopSettings';
import PrintNodeSettings from './PrintNodeSettings';
import PakowalniaSettings from './PakowalniaSettings';
import EmpikSettings from './EmpikSettings';
import AliOrdersPanel from './AliOrdersPanel';
import PsOrdersPanel from './PsOrdersPanel';
import OrderMessagesSettings from './OrderMessagesSettings';
import OrdersStats from './OrdersStats';

type Props = { API: string };
const auth = () => ({ Authorization: 'Bearer ' + getAuthToken() });
const ALI_CALLBACK = 'https://www.openkontakt.com/api/settings/aliexpress/callback';

const card: any = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'center' };

function Badge({ ok, text }: { ok: boolean | null; text: string }) {
  const c = ok === true ? { c: '#15803d', bg: '#f0fdf4', b: '#bbf7d0' } : ok === false ? { c: '#6b7280', bg: '#f3f4f6', b: '#e5e7eb' } : { c: '#b45309', bg: '#fffbeb', b: '#fde68a' };
  return <span style={{ fontSize: 11, fontWeight: 600, color: c.c, background: c.bg, border: '1px solid ' + c.b, borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' }}>{text}</span>;
}

export default function OrdersSettings({ API }: Props) {
  const [sub, setSub] = useState('plugins');
  const [plugin, setPlugin] = useState<string | null>(null);
  const [ps, setPs] = useState<any>(null);
  const [pn, setPn] = useState<any>(null);
  const [pk, setPk] = useState<any>(null);
  const loadPk = () => { axios.get(API + '/settings/pakowalnia', { headers: auth() }).then((r: any) => setPk(r.data)).catch(() => setPk(null)); };
  const loadPn = () => { axios.get(API + '/settings/printnode', { headers: auth() }).then((r: any) => setPn(r.data)).catch(() => setPn(null)); };
  const [em, setEm] = useState<any>(null);
  const loadEm = () => { axios.get(API + '/empik-settings', { headers: auth() }).then((r: any) => setEm(r.data)).catch(() => setEm(null)); };
  const [copied, setCopied] = useState(false);
  const [ali, setAli] = useState<any>(null);
  const [aliKey, setAliKey] = useState('');
  const [aliSecret, setAliSecret] = useState('');
  const [aliBusy, setAliBusy] = useState(false);
  const [aliMsg, setAliMsg] = useState('');
  const [aliErr, setAliErr] = useState('');
  const [aliTesting, setAliTesting] = useState(false);
  const [aliTest, setAliTest] = useState<any>(null);

  const [aliWaiting, setAliWaiting] = useState(false);

  const connectShop = () => {
    setAliErr(''); setAliMsg('');
    axios.get(API + '/settings/aliexpress/authorize-url', { headers: auth() })
      .then((r: any) => {
        window.open(r.data.url, '_blank', 'noopener');
        setAliWaiting(true);
        let n = 0;
        const t = window.setInterval(() => {
          n++;
          axios.get(API + '/settings/aliexpress', { headers: auth() }).then((x: any) => {
            if (x.data && x.data.shop_connected) {
              window.clearInterval(t); setAliWaiting(false); setAli(x.data);
              setAliMsg('Sklep AliExpress połączony.');
            }
          }).catch(() => undefined);
          if (n >= 100) { window.clearInterval(t); setAliWaiting(false); }
        }, 3000);
      })
      .catch((e: any) => setAliErr((e.response && e.response.data && e.response.data.detail) || 'Nie udało się przygotować autoryzacji.'));
  };

  const disconnectShop = () => {
    if (!window.confirm('Odłączyć sklep AliExpress od OPENkontakt?')) return;
    axios.post(API + '/settings/aliexpress/disconnect-shop', {}, { headers: auth() })
      .then(() => { setAliMsg('Sklep odłączony.'); loadAli(); })
      .catch((e: any) => setAliErr((e.response && e.response.data && e.response.data.detail) || 'Nie udało się odłączyć sklepu.'));
  };

  const testAli = () => {
    setAliTesting(true); setAliTest(null); setAliErr(''); setAliMsg('');
    axios.post(API + '/settings/aliexpress/test', {}, { headers: auth() })
      .then((r: any) => setAliTest(r.data))
      .catch((e: any) => setAliTest({ ok: false, message: (e.response && e.response.data && e.response.data.detail) || 'Test nieudany.' }))
      .finally(() => setAliTesting(false));
  };

  const [dpd, setDpd] = useState<any>(null);
  const [dpdForm, setDpdForm] = useState({ login: '', master_fid: '', password: '', label_type: 'PDF_A4' });
  const [dpdBusy, setDpdBusy] = useState(false);
  const [dpdMsg, setDpdMsg] = useState('');
  const [dpdErr, setDpdErr] = useState('');
  const [dpdTest, setDpdTest] = useState<any>(null);
  const [dpdTesting, setDpdTesting] = useState(false);

  const loadDpd = () => {
    axios.get(API + '/settings/dpd', { headers: auth() })
      .then((r: any) => {
        setDpd(r.data);
        setDpdForm((f) => ({ ...f, login: r.data.login || '', master_fid: r.data.master_fid || '', label_type: r.data.label_type || 'PDF_A4', password: '' }));
      })
      .catch(() => setDpd(null));
  };

  const saveDpd = () => {
    setDpdBusy(true); setDpdMsg(''); setDpdErr(''); setDpdTest(null);
    axios.post(API + '/settings/dpd', dpdForm, { headers: auth() })
      .then((r: any) => { setDpd(r.data); setDpdForm((f) => ({ ...f, password: '' })); setDpdMsg('Zapisano dane DPD.'); })
      .catch((e: any) => setDpdErr((e.response && e.response.data && e.response.data.detail) || 'Nie udało się zapisać danych DPD.'))
      .finally(() => setDpdBusy(false));
  };

  const testDpd = () => {
    setDpdTesting(true); setDpdTest(null); setDpdMsg(''); setDpdErr('');
    axios.post(API + '/settings/dpd/test', {}, { headers: auth() })
      .then((r: any) => setDpdTest(r.data))
      .catch((e: any) => setDpdTest({ ok: false, message: (e.response && e.response.data && e.response.data.detail) || 'Test nieudany.' }))
      .finally(() => setDpdTesting(false));
  };

  const deleteDpd = () => {
    if (!window.confirm('Usunąć zapisane dane DPD?')) return;
    axios.delete(API + '/settings/dpd', { headers: auth() })
      .then(() => { setDpdMsg('Usunięto dane DPD.'); setDpdTest(null); loadDpd(); })
      .catch((e: any) => setDpdErr((e.response && e.response.data && e.response.data.detail) || 'Nie udało się usunąć danych.'));
  };

  const loadAli = () => {
    axios.get(API + '/settings/aliexpress', { headers: auth() }).then((r: any) => setAli(r.data)).catch(() => setAli(null));
  };

  useEffect(() => {
    axios.get(API + '/settings/prestashop', { headers: auth() }).then((r: any) => setPs(r.data)).catch(() => setPs(null));
    loadAli();
    loadDpd();
    loadPn();
    loadPk();
    loadEm();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, plugin]);

  const saveAli = () => {
    setAliBusy(true); setAliMsg(''); setAliErr('');
    axios.post(API + '/settings/aliexpress/app', { app_key: aliKey, app_secret: aliSecret }, { headers: auth() })
      .then((r: any) => { setAli(r.data); setAliKey(''); setAliSecret(''); setAliMsg('Zapisano App Key i App Secret.'); })
      .catch((e: any) => setAliErr((e.response && e.response.data && e.response.data.detail) || 'Nie udało się zapisać danych aplikacji.'))
      .finally(() => setAliBusy(false));
  };

  const deleteAli = () => {
    if (!window.confirm('Usunąć zapisane App Key i App Secret AliExpress?')) return;
    axios.delete(API + '/settings/aliexpress', { headers: auth() })
      .then(() => { setAliMsg('Usunięto dane aplikacji.'); loadAli(); })
      .catch((e: any) => setAliErr((e.response && e.response.data && e.response.data.detail) || 'Nie udało się usunąć danych.'));
  };

  const copy = (t: string) => {
    try { navigator.clipboard.writeText(t).then(() => { setCopied(true); window.setTimeout(() => setCopied(false), 1500); }); } catch (e) { /* ignore */ }
  };

  const plugins = [
    { id: 'prestashop', name: 'PrestaShop', desc: 'Sklep internetowy: produkty, ceny, metody i koszty dostaw do ręcznych zamówień.', icon: '🛒',
      badge: <Badge ok={!!(ps && ps.configured)} text={ps && ps.configured ? 'Połączono' : 'Niepołączono'} /> },
    { id: 'aliexpress', name: 'AliExpress', desc: 'Import i obsługa zamówień ze sklepu AliExpress.', icon: '🅰️',
      badge: <Badge ok={ali && ali.shop_connected ? true : (ali && ali.app_configured ? null : false)} text={ali && ali.shop_connected ? 'Połączono' : (ali && ali.app_configured ? 'Klucze zapisane' : 'Nie skonfigurowano')} /> },
    { id: 'dpd', name: 'DPD', desc: 'Kurier DPD Polska: dane do API (etykiety i przesyłki).', icon: '📦',
      badge: <Badge ok={dpd && dpd.configured ? true : false} text={dpd && dpd.configured ? 'Skonfigurowano' : 'Nie skonfigurowano'} /> },
    { id: 'printnode', name: 'PrintNode', desc: 'Ponowny wydruk etykiet i faktur z zamówienia na drukarce przez PrintNode.', icon: '🖨️',
      badge: <Badge ok={pn && pn.configured ? ((pn.label_printer_id || pn.invoice_printer_id) ? true : null) : false} text={pn && pn.configured ? ((pn.label_printer_id || pn.invoice_printer_id) ? 'Skonfigurowano' : 'Wybierz drukarki') : 'Nie skonfigurowano'} /> },
    { id: 'pakowalnia', name: 'Pakowalnia', desc: 'noboco: przycisk „Paragon” — paragon w kasie, wydruk i podpięcie pod zamówienie.', icon: '🧾',
      badge: <Badge ok={pk && pk.configured ? true : false} text={pk && pk.configured ? 'Skonfigurowano' : 'Nie skonfigurowano'} /> },
    { id: 'empik', name: 'Empik', desc: 'Empik Marketplace (Mirakl): połączenie konta sprzedawcy, oferty i zamówienia.', icon: '📚',
      badge: <Badge ok={em && em.connected ? true : false} text={em && em.connected ? 'Połączono' : 'Niepołączono'} /> },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, borderBottom: '1px solid #e5e7eb' }}>
        {[['plugins', 'Wtyczki'], ['messages', 'Wiadomości'], ['stats', 'Statystyki']].map(([id, label]) => (
          <div key={id} onClick={() => { setSub(id); setPlugin(null); }}
            style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13, fontWeight: sub === id ? 600 : 400, color: sub === id ? '#1d4ed8' : '#374151', borderBottom: sub === id ? '2px solid #1d4ed8' : '2px solid transparent', marginBottom: -1 }}>{label}</div>
        ))}
      </div>

      {sub === 'messages' ? <OrderMessagesSettings API={API} /> : null}
      {sub === 'stats' ? <OrdersStats API={API} /> : null}

      {sub === 'plugins' && !plugin && (
        <div>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>Wtyczki łączą moduł Zamówienia z innymi kanałami sprzedaży. Kliknij wtyczkę, aby ją skonfigurować.</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {plugins.map((p) => (
              <div key={p.id} onClick={() => setPlugin(p.id)} style={card}>
                <div style={{ fontSize: 28, width: 40, textAlign: 'center' }}>{p.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{p.name}</span>{p.badge}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{p.desc}</div>
                </div>
                <span style={{ color: '#9ca3af', fontSize: 18 }}>›</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {sub === 'plugins' && plugin ? (
        <div>
          <button onClick={() => setPlugin(null)} style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', fontSize: 13, cursor: 'pointer', marginBottom: 12 }}>← Wtyczki</button>
          {plugin === 'prestashop' ? <><PrestaShopSettings API={API} /><PsOrdersPanel API={API} /></> : null}
          {plugin === 'printnode' ? <PrintNodeSettings API={API} onChange={loadPn} /> : null}
          {plugin === 'pakowalnia' ? <PakowalniaSettings API={API} onChange={loadPk} /> : null}
          {plugin === 'empik' ? <EmpikSettings API={API} onChange={loadEm} /> : null}
          {plugin === 'dpd' ? (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>DPD Polska</div>
              <div style={{ fontSize: 13, color: '#374151', marginBottom: 12 }}>Dane do API DPD z umowy (login, MasterFID i hasło do webservice DPD) oraz domyślny typ etykiety.</div>
              {dpdErr ? <div style={{ background: '#fef2f2', border: '1px solid #fecdd3', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 10 }}>{dpdErr}</div> : null}
              {dpdMsg ? <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 10 }}>{dpdMsg}</div> : null}
              {dpdTest ? <div style={{ background: dpdTest.ok ? '#f0fdf4' : '#fef2f2', border: '1px solid ' + (dpdTest.ok ? '#bbf7d0' : '#fecdd3'), color: dpdTest.ok ? '#15803d' : '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 10 }}>{dpdTest.ok ? '✓ ' : '✕ '}{String(dpdTest.message || '')}</div> : null}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10, maxWidth: 620 }}>
                <div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Login</div>
                  <input value={dpdForm.login} onChange={(e: any) => setDpdForm((f) => ({ ...f, login: e.target.value }))} autoComplete="off" style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>MasterFID</div>
                  <input value={dpdForm.master_fid} onChange={(e: any) => setDpdForm((f) => ({ ...f, master_fid: e.target.value }))} autoComplete="off" style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Hasło</div>
                  <input type="password" value={dpdForm.password} onChange={(e: any) => setDpdForm((f) => ({ ...f, password: e.target.value }))} autoComplete="new-password"
                    placeholder={dpd && dpd.password_saved ? 'zapisane — wpisz, aby zmienić' : 'hasło do API DPD'} style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Typ etykiety</div>
                  <select value={dpdForm.label_type} onChange={(e: any) => setDpdForm((f) => ({ ...f, label_type: e.target.value }))} style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}>
                    {Object.keys((dpd && dpd.label_types) || { PDF_A4: 'PDF - strona A4' }).map((k) => <option key={k} value={k}>{String(((dpd && dpd.label_types) || { PDF_A4: 'PDF - strona A4' })[k])}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={saveDpd} disabled={dpdBusy}
                  style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid #1d4ed8', background: '#1d4ed8', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: dpdBusy ? 0.5 : 1 }}>{dpdBusy ? 'Zapisywanie…' : 'Zapisz'}</button>
                {dpd && dpd.configured ? (
                  <button onClick={testDpd} disabled={dpdTesting}
                    style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', fontSize: 13, cursor: 'pointer', opacity: dpdTesting ? 0.5 : 1 }}>{dpdTesting ? '⏳ Testowanie…' : 'Testuj połączenie'}</button>
                ) : null}
                {dpd && dpd.configured ? (
                  <button onClick={deleteDpd} style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid #fecdd3', background: '#fff', color: '#b91c1c', fontSize: 13, cursor: 'pointer' }}>Usuń dane</button>
                ) : null}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 10 }}>Hasło jest przechowywane w zaszyfrowanej postaci i nie jest wyświetlane po zapisaniu.</div>
            </div>
          ) : null}
          {plugin === 'aliexpress' ? (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>AliExpress</div>
              <div style={{ fontSize: 13, color: '#374151', marginBottom: 12 }}>
                Krok 1: dane aplikacji z AliExpress Open Platform (App Key i App Secret). Krok 2: połączenie sklepu AliExpress (logowanie w AliExpress i zgoda na dostęp).
              </div>
              {aliErr ? <div style={{ background: '#fef2f2', border: '1px solid #fecdd3', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 10 }}>{aliErr}</div> : null}
              {aliMsg ? <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 10 }}>{aliMsg}</div> : null}
              {aliTest ? (
                <div style={{ background: aliTest.ok ? '#f0fdf4' : '#fef2f2', border: '1px solid ' + (aliTest.ok ? '#bbf7d0' : '#fecdd3'), color: aliTest.ok ? '#15803d' : '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 10 }}>
                  {aliTest.ok ? '✓ ' : '✕ '}{String(aliTest.message || '')}
                </div>
              ) : null}
              {ali && ali.app_configured ? (
                <div style={{ fontSize: 13, marginBottom: 10, color: '#374151' }}>
                  Zapisane: App Key <code>{String(ali.app_key_masked || '')}</code> · App Secret <code>{String(ali.app_secret_masked || '')}</code>
                  <button onClick={testAli} disabled={aliTesting} style={{ marginLeft: 10, padding: '3px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 12, cursor: 'pointer', opacity: aliTesting ? 0.5 : 1 }}>{aliTesting ? '⏳ Testowanie…' : 'Testuj połączenie'}</button>
                  <button onClick={deleteAli} style={{ marginLeft: 6, padding: '3px 10px', borderRadius: 6, border: '1px solid #fecdd3', background: '#fff', color: '#b91c1c', fontSize: 12, cursor: 'pointer' }}>Usuń</button>
                </div>
              ) : null}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>App Key</div>
                  <input value={aliKey} onChange={(e: any) => setAliKey(e.target.value)} placeholder={ali && ali.app_configured ? 'zapisany — wpisz, aby zmienić' : 'np. 501234'} autoComplete="off"
                    style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>App Secret</div>
                  <input type="password" value={aliSecret} onChange={(e: any) => setAliSecret(e.target.value)} placeholder={ali && ali.app_configured ? 'zapisany — wpisz, aby zmienić' : 'App Secret'} autoComplete="new-password"
                    style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
              </div>
              <button onClick={saveAli} disabled={aliBusy || !aliKey.trim() || !aliSecret.trim()}
                style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid #1d4ed8', background: '#1d4ed8', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (aliBusy || !aliKey.trim() || !aliSecret.trim()) ? 0.5 : 1, marginBottom: 14 }}>{aliBusy ? 'Zapisywanie…' : 'Zapisz dane aplikacji'}</button>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 12 }}>App Secret jest przechowywany w zaszyfrowanej postaci i nie jest wyświetlany po zapisaniu.</div>
              {ali && ali.app_configured ? (
                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12, marginBottom: 14 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Krok 2: sklep AliExpress</div>
                  {ali.shop_connected ? (
                    <div style={{ fontSize: 13, color: '#374151' }}>
                      <div>Połączony sklep: <b>{String(ali.user_nick || ali.account || ali.seller_id || '—')}</b>{ali.seller_id ? <span style={{ color: '#6b7280' }}> · seller ID {String(ali.seller_id)}</span> : null}</div>
                      <div style={{ color: '#6b7280', fontSize: 12, marginTop: 4 }}>Token ważny do: {String(ali.token_expires || '—')} · odnowienie możliwe do: {String(ali.refresh_expires || '—')} (odnawiany automatycznie)</div>
                      <button onClick={disconnectShop} style={{ marginTop: 8, padding: '5px 12px', borderRadius: 6, border: '1px solid #fecdd3', background: '#fff', color: '#b91c1c', fontSize: 12, cursor: 'pointer' }}>Odłącz sklep</button>
                      <AliOrdersPanel API={API} />
                    </div>
                  ) : (
                    <div>
                      <button onClick={connectShop} disabled={aliWaiting}
                        style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #ea580c', background: '#ea580c', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: aliWaiting ? 0.6 : 1 }}>{aliWaiting ? '⏳ Czekam na autoryzację w AliExpress…' : 'Połącz sklep AliExpress'}</button>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>Otworzy się nowa karta z logowaniem AliExpress. Zaloguj się kontem sprzedawcy i zatwierdź dostęp — ta strona sama wykryje połączenie.</div>
                    </div>
                  )}
                </div>
              ) : null}
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Callback URL (do formularza aplikacji w AliExpress)</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <code style={{ background: '#f3f4f6', borderRadius: 6, padding: '6px 10px', fontSize: 12 }}>{ALI_CALLBACK}</code>
                <button onClick={() => copy(ALI_CALLBACK)} style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', fontSize: 12, cursor: 'pointer' }}>{copied ? '✓ Skopiowano' : 'Kopiuj'}</button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
